import { ColumnDef } from "@tanstack/react-table";
import {
  DropdownMenu,
  DropdownMenuOption,
  ExtendDeadlineModalTwo,
  PaginationProps,
  InviteCandidatesModal,
  DeleteConfirmationModal,
} from "components/";
import { Button } from "components/button";
import { DataTable } from "components/data-table";
import styles from "./styles.module.scss";
import { useState, useMemo } from "react";
import { useUpdateAssessmentStatus, useInviteCandidates, useInviteBulkCandidates, useToast, useDeleteAssessment, InviteCandidatesPayload } from "hooks";
import { useNavigate } from "react-router-dom";
import { Routes } from "router";
import { copyToClipBoard } from "helpers";

export interface ManageJobsTableItem {
  id: string;
  assessmentId?: string;
  role: string;
  publishedDate: string;
  viewsVisits: number;
  applications: number;
  status: "Active" | "Paused" | "Scheduled" | "Closed" | "Draft";
  currentStage?: string;
  startDate?: string;
  endDate?: string;
  deadline?: string;
  deadlineRaw?: string | null;
  assessmentLink?: string;
}

interface ManageJobsTableProps {
  tableBodyItems: ManageJobsTableItem[];
  handleView: (id: string, assessmentId?: string) => void;
  /** @deprecated No longer used */
  tableBodyItemClassName?: string;
  /** @deprecated No longer used */
  tableBodyRowClassName?: string;
  pagination?: PaginationProps;
  currentFilter: string;
  emptyState?: React.ReactNode;
}

const CELL_BASE = "text-[12px] text-[#1D2939] flex-1 min-w-0";
const SERIAL_CELL = "text-[12px] text-[#1D2939] w-8 flex-shrink-0";
const TITLE_CELL = "text-[12px] text-[#1D2939] flex-[2_1_0] min-w-0 lg:flex-[2_1_0] flex-[1_1_0]";
const MENU_CELL = "w-7 flex-shrink-0";

const ManageJobsTable: React.FC<ManageJobsTableProps> = ({
  tableBodyItems,
  handleView,
  pagination,
  currentFilter,
  emptyState,
}) => {
  const navigate = useNavigate();
  const { openToast } = useToast();
  const { updateStatus } = useUpdateAssessmentStatus();
  const { mutate: inviteCandidates, isPending: isInviting } = useInviteCandidates();
  const { mutate: inviteBulkCandidates, isPending: isBulkInviting } = useInviteBulkCandidates();
  const { deleteAssessment, isLoading: isDeleting } = useDeleteAssessment();

  const [extendDeadlineModal, setExtendDeadlineModal] = useState<{
    show: boolean;
    assessmentId: string;
    assessmentTitle: string;
    reopenOnSuccess?: boolean;
  }>({ show: false, assessmentId: "", assessmentTitle: "" });

  const [inviteModal, setInviteModal] = useState<{
    show: boolean;
    assessmentId: string;
  }>({ show: false, assessmentId: "" });

  const [deleteModal, setDeleteModal] = useState<{
    show: boolean;
    assessmentId: string;
    assessmentTitle: string;
  }>({ show: false, assessmentId: "", assessmentTitle: "" });

  const handleCopyAssessmentLink = (link: string) => {
    copyToClipBoard({
      link,
      onSuccess: () => openToast({ type: 'success', show: true, heading: 'Success', text: 'Assessment link copied to clipboard' }),
      onError: () => openToast({ type: 'error', show: true, heading: 'Error', text: 'Failed to copy assessment link' }),
    });
  };

  const handleSaveInvites = (payload: { candidates?: InviteCandidatesPayload["candidates"]; file?: File }) => {
    if (payload.file) {
      inviteBulkCandidates(
        { assessmentId: inviteModal.assessmentId, file: payload.file },
        { onSuccess: () => setInviteModal({ show: false, assessmentId: "" }) }
      );
    } else if (payload.candidates) {
      inviteCandidates(
        { assessmentId: inviteModal.assessmentId, candidates: payload.candidates },
        { onSuccess: () => setInviteModal({ show: false, assessmentId: "" }) }
      );
    }
  };

  const filterLower = currentFilter?.toLowerCase();
  const isDraftView = filterLower === "draft";
  const isScheduledView = filterLower === "scheduled";
  const isActiveView = filterLower === "active";
  const isPausedView = filterLower === "paused";
  const isClosedView = filterLower === "closed";

  // ──────────────────────────────────────────────────────────────
  // Helpers to build dropdown options per item
  // ──────────────────────────────────────────────────────────────
  const buildOptions = (item: ManageJobsTableItem): DropdownMenuOption[] => {
    const id = item.assessmentId || item.id;
    const isDeadlineExpired = !!item.deadlineRaw && new Date(item.deadlineRaw) < new Date();

    const draftOptions: DropdownMenuOption[] = [
      { text: <>Edit</>, onClick: () => navigate(`${Routes.publishAssessment}?id=${id}`), key: "edit" },
      { text: <span className={styles.delete}>Delete</span>, onClick: () => setDeleteModal({ show: true, assessmentId: id, assessmentTitle: item.role }), key: "delete" },
    ];
    const scheduledOptions: DropdownMenuOption[] = [
      { text: <>Edit Date</>, onClick: () => setExtendDeadlineModal({ show: true, assessmentId: id, assessmentTitle: item.role }), key: "edit" },
      { text: <>Invite candidates</>, onClick: () => setInviteModal({ show: true, assessmentId: id }), key: "invite" },
      { text: <>Copy assessment link</>, onClick: () => handleCopyAssessmentLink(item.assessmentLink || ""), key: "copy" },
      { text: <span className={styles.delete}>Close</span>, onClick: () => updateStatus({ assessmentId: id, status: 'closed' }), key: "close" },
    ];
    const activeOptions: DropdownMenuOption[] = [
      { text: <>Copy assessment link</>, onClick: () => handleCopyAssessmentLink(item.assessmentLink || ""), key: "copy" },
      { text: <>Invite candidates</>, onClick: () => setInviteModal({ show: true, assessmentId: id }), key: "invite" },
      { text: <>Extend deadlines</>, onClick: () => setExtendDeadlineModal({ show: true, assessmentId: id, assessmentTitle: item.role }), key: "extension" },
      { text: <>View results</>, onClick: () => navigate(Routes.reviewTalents(id)), key: "view" },
      { text: <>Pause</>, onClick: () => updateStatus({ assessmentId: id, status: 'paused' }), key: "pause" },
      { text: <span className={styles.delete}>Close</span>, onClick: () => updateStatus({ assessmentId: id, status: 'closed' }), key: "close" },
    ];
    const pausedOptions: DropdownMenuOption[] = [
      { text: <>Copy assessment link</>, onClick: () => handleCopyAssessmentLink(item.assessmentLink || ""), key: "copy" },
      { text: <>Invite candidates</>, onClick: () => setInviteModal({ show: true, assessmentId: id }), key: "invite" },
      ...(isDeadlineExpired
        ? [{ text: <>Extend deadline to resume</>, onClick: () => setExtendDeadlineModal({ show: true, assessmentId: id, assessmentTitle: item.role }), key: "resume" }]
        : [{ text: <>Resume assessment</>, onClick: () => updateStatus({ assessmentId: id, status: 'active' }), key: "resume" }]
      ),
      { text: <>View results</>, onClick: () => navigate(Routes.reviewTalents(id)), key: "view" },
      { text: <span className={styles.delete}>Close</span>, onClick: () => updateStatus({ assessmentId: id, status: 'closed' }), key: "close" },
    ];
    const closedOptions: DropdownMenuOption[] = [
      { text: <>View results</>, onClick: () => navigate(Routes.reviewTalents(id)), key: "view" },
      { text: <>Copy assessment link</>, onClick: () => handleCopyAssessmentLink(item.assessmentLink || ""), key: "copy" },
      { text: <>Extend deadline to re-open</>, onClick: () => setExtendDeadlineModal({ show: true, assessmentId: id, assessmentTitle: item.role, reopenOnSuccess: true }), key: "re-open" },
    ];

    const statusLower = item.status?.toLowerCase();
    if (isDraftView || statusLower === "draft") return draftOptions;
    if (isScheduledView || statusLower === "scheduled") return scheduledOptions;
    if (isActiveView || statusLower === "active") return activeOptions;
    if (isPausedView || statusLower === "paused") return pausedOptions;
    if (isClosedView || statusLower === "closed") return closedOptions;
    return draftOptions;
  };

  // ──────────────────────────────────────────────────────────────
  // Column definitions — 3 layouts
  // ──────────────────────────────────────────────────────────────
  const defaultColumns: ColumnDef<ManageJobsTableItem, unknown>[] = [
    { id: "sn", header: "S/N", meta: { className: SERIAL_CELL }, cell: ({ row }) => <span>{row.index + 1}</span> },
    { id: "role", header: "Role", meta: { className: TITLE_CELL }, cell: ({ row }) => <span className="capitalize font-medium text-[13px]">{row.original.role}</span> },
    { id: "date", header: "Published date", meta: { className: CELL_BASE }, cell: ({ row }) => <span>{row.original.publishedDate}</span> },
    { id: "views", header: "Number of Views/Visits", meta: { className: CELL_BASE }, cell: ({ row }) => <span>{row.original.viewsVisits}</span> },
    { id: "apps", header: "Number of Application", meta: { className: `${CELL_BASE} text-[#1D2939]` }, cell: ({ row }) => <span>{row.original.applications}</span> },
    { id: "deadline", header: "Deadline", meta: { className: CELL_BASE }, cell: ({ row }) => <span>{row.original.deadline || '-'}</span> },
    {
      id: "status", header: "Status", meta: { className: CELL_BASE },
      cell: ({ row }) => (
        <span className={`${styles.status} ${styles[row.original.status.toLowerCase()]}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      id: "actions", header: "", meta: { className: MENU_CELL },
      cell: ({ row }) => (
        <span className={styles.menuSec} onClick={(e) => e.stopPropagation()}>
          <DropdownMenu options={buildOptions(row.original)} ellipsisClassName={styles.ellipsis} />
        </span>
      ),
    },
  ];

  const draftColumns: ColumnDef<ManageJobsTableItem, unknown>[] = [
    { id: "sn", header: "S/N", meta: { className: SERIAL_CELL }, cell: ({ row }) => <span>{row.index + 1}</span> },
    { id: "role", header: "Role", meta: { className: TITLE_CELL }, cell: ({ row }) => <span className="capitalize font-medium text-[13px]">{row.original.role}</span> },
    { id: "date", header: "Date created", meta: { className: CELL_BASE }, cell: ({ row }) => <span>{row.original.publishedDate}</span> },
    { id: "stage", header: "Current stage", meta: { className: CELL_BASE }, cell: ({ row }) => <span>{row.original.currentStage || "Job Description"}</span> },
    {
      id: "action", header: "Action", meta: { className: "flex-shrink-0" },
      cell: ({ row }) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Button
            type="fill"
            onClick={() => navigate(`${Routes.publishAssessment}?id=${row.original.assessmentId || row.original.id}`)}
            className={styles.continueButton}
          >
            Edit
          </Button>
        </span>
      ),
    },
  ];

  const scheduledColumns: ColumnDef<ManageJobsTableItem, unknown>[] = [
    { id: "sn", header: "S/N", meta: { className: SERIAL_CELL }, cell: ({ row }) => <span>{row.index + 1}</span> },
    { id: "role", header: "Assessment title", meta: { className: TITLE_CELL }, cell: ({ row }) => <span className="capitalize font-medium text-[13px]">{row.original.role}</span> },
    { id: "start", header: "Start date", meta: { className: CELL_BASE }, cell: ({ row }) => <span>{row.original.startDate || row.original.publishedDate}</span> },
    { id: "end", header: "End date", meta: { className: CELL_BASE }, cell: ({ row }) => <span>{row.original.endDate || row.original.publishedDate}</span> },
    {
      id: "status", header: "Status", meta: { className: CELL_BASE },
      cell: ({ row }) => (
        <span className={`${styles.status} ${styles[row.original.status.toLowerCase()]}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      id: "actions", header: "", meta: { className: MENU_CELL },
      cell: ({ row }) => (
        <span className={styles.menuSec} onClick={(e) => e.stopPropagation()}>
          <DropdownMenu options={buildOptions(row.original)} ellipsisClassName={styles.ellipsis} />
        </span>
      ),
    },
  ];

  const columns = useMemo(() => {
    if (isDraftView) return draftColumns;
    if (isScheduledView) return scheduledColumns;
    return defaultColumns;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraftView, isScheduledView]);

  return (
    <>
      <DataTable
        columns={columns}
        data={tableBodyItems}
        pagination={pagination}
        emptyState={emptyState}
        onRowClick={(item) => {
          const isDraftItem = item.status?.toLowerCase() === "draft";
          const id = item.assessmentId || item.id;
          if (isDraftItem) {
            navigate(`${Routes.publishAssessment}?id=${id}`);
          } else {
            handleView(item.id, item.assessmentId);
          }
        }}
        rowBaseClassName={(_, index) =>
          `text-[12px] border-t border-[#EAECF0] ${
            index % 2 === 0 ? "bg-[#F9FAFB]" : "bg-white"
          }`
        }
        cellBaseClassName="px-4 py-3"
        headerCellClassName="px-4 py-3 text-left font-medium"
        headerClassName="bg-white text-[#1D2939] text-[12px]"
      />

      <ExtendDeadlineModalTwo
        show={extendDeadlineModal.show}
        onClose={() => setExtendDeadlineModal({ show: false, assessmentId: "", assessmentTitle: "" })}
        assessmentId={extendDeadlineModal.assessmentId}
        assessmentTitle={extendDeadlineModal.assessmentTitle}
        onSuccess={extendDeadlineModal.reopenOnSuccess
          ? () => updateStatus({ assessmentId: extendDeadlineModal.assessmentId, status: 'active' })
          : undefined
        }
      />

      <InviteCandidatesModal
        isOpen={inviteModal.show}
        onClose={() => setInviteModal({ show: false, assessmentId: "" })}
        onSave={handleSaveInvites}
        isLoading={isInviting || isBulkInviting}
        assessmentId={inviteModal.assessmentId}
      />

      <DeleteConfirmationModal
        show={deleteModal.show}
        onClose={() => setDeleteModal({ show: false, assessmentId: "", assessmentTitle: "" })}
        onConfirm={() => deleteAssessment(deleteModal.assessmentId, { onSuccess: () => setDeleteModal({ show: false, assessmentId: "", assessmentTitle: "" }) })}
        title="Delete Assessment"
        message={`Are you sure you want to delete "${deleteModal.assessmentTitle}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        type="danger"
      />
    </>
  );
};

export { ManageJobsTable };
